import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { ProgramId, ProgramTargetResponse, TargetId, ProgramStepCompletedResponse, StepCompletedId } from '@business/entities/programs';
import { ProgramTargetsAPI } from '@business/api/program_targets';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { UserId } from '@business/entities/account';

export class ProgramTargetBloc extends BaseBloc {
  private readonly programTargetRepo = new Repository<ProgramTargetResponse>({
    getItemId: item => item.id,
  });

  readonly allProgramTargets$ = this.programTargetRepo.items$;
  readonly selectProgramTarget = this.programTargetRepo.selectItem;

  onReset() {
    this.programTargetRepo.reset();
  }

  fetchProgramTargets = (id: ProgramId) => {
    return ProgramTargetsAPI.fetchProgramTargets(id).pipe(
      this.programTargetRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_program_target_error') }),
    );
  };

  fetchProgramTarget = (targetId: TargetId) => {
    return ProgramTargetsAPI.fetchProgramTarget(targetId).pipe(
      this.programTargetRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_program_target_error') }),
    );
  };

  deleteProgramTarget = (id: TargetId) => {
    return showDeleteConfirmation('Delete Program Target', 'Do you really want to remove this Target?').pipe(
      switchMap(() => ProgramTargetsAPI.deleteProgramTarget(id)),
      this.programTargetRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_program_target'),
      }),
    );
  };

  createProgramTarget = (data: Partial<ProgramTargetResponse>) => {
    return ProgramTargetsAPI.createProgramTarget(data).pipe(
      this.programTargetRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_program_target'),
      }),
    );
  };

  updateProgramTarget = (id: TargetId, data: Partial<ProgramTargetResponse>) => {
    return ProgramTargetsAPI.updateProgramTarget(id, data).pipe(
      this.programTargetRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_program_target'),
      }),
    );
  };

  sortProgramTarget = (target: ProgramTargetResponse, toColumn: any, toPosition: number) => {
    return ProgramTargetsAPI.sortProgramTarget(target, toColumn, toPosition).pipe(
      this.programTargetRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('update_program_target'),
      }),
    );
  };

  markProgramTargetCompleted = (val: boolean, targetId: TargetId, userId: UserId) => {
    return ProgramTargetsAPI.markProgramTargetCompleted(val, targetId, userId).pipe(
      this.programTargetRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('complete_program_target'),
      }),
    );
  };

  createComplianceReport = (data: Partial<ProgramStepCompletedResponse>) => {
    return ProgramTargetsAPI.submitComplianceReport(data).pipe(
      this.programTargetRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('complete_program_target'),
      }),
    );
  };

  sendProgramNudge = (stepId: TargetId) => {
    return ProgramTargetsAPI.sendProgramNudge(stepId).pipe(
      handleMessage({
        type: requestMessage('complete_program_target_nudge'),
      }),
    );
  };

  approveTargetReport = (stepCompletedId: StepCompletedId, userId: UserId) => {
    return ProgramTargetsAPI.approveTargetReport(stepCompletedId, userId).pipe(
      this.programTargetRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('approve_program_target_report'),
      }),
    );
  };

  rejectTargetReport = (stepCompletedId: StepCompletedId, userId: UserId) => {
    return ProgramTargetsAPI.rejectTargetReport(stepCompletedId, userId).pipe(
      this.programTargetRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('reject_program_target_report'),
      }),
    );
  };

  fetchUserProgramTargets = (id: UserId) => {
    return ProgramTargetsAPI.fetchProgramUserTargets(id).pipe(
      this.programTargetRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_program_target_error') }),
    );
  };
}
